import mobile from 'is-mobile';
import { Lethargy } from 'lethargy';

import {
    breakpoints,
    isScreenAtLeast,
    scrollTo,
} from '../../common/utils';

import els from './elements';
import map from './map';

const {
    computed,
    createApp,
    ref,
    onMounted,
    nextTick,
} = Vue;

const { useWindowSize } = VueUse;

const RESIZE_TIMEOUT = 600;

let lethargy = null;

const setupApp = () => {

    const { width } = useWindowSize();

    const isMobile = computed(() => (
        width.value < breakpoints.md
    ));

    const loading = ref(true);

    const resizing = ref(false);

    const selectedState = ref(null);

    const hoveredCity = ref(null);

    const selectedCity = ref(null);

    const selectedPropertyId = ref(0);

    const isCityDropdownActive = ref(false);

    const { properties = [] } = window.pageData;

    const mode = els.$app().attr('data-requested-mode');

    const isInLinkedMode = () => mode === 'linked';

    const requestedInitialZoom = (() => {

        const $app = els.$app();
        const scale = $app.attr('data-requested-initial-zoom-scale');
        const xPercent = $app.attr('data-requested-initial-zoom-x-percent');
        const yPercent = $app.attr('data-requested-initial-zoom-y-percent');

        const parameters = { scale, xPercent, yPercent };

        return (
            Object.keys(parameters).reduce(
                (relevant, key) => {
                    if (parameters[key].length > 0) {
                        relevant[key] = parameters[key];
                    }
                    return relevant;
                },
                {}
            )
        );
    })();

    const propertyCities = computed(() => (
        properties
            .reduce(
                (cities, property) => {
                    const cityIds = cities.map((city) => city.id);
                    if (!cityIds.includes(property.city.id)) {
                        cities.push(property.city);
                    }
                    return cities;
                },
                []
            )
    ));

    const nameForStateAbbreviation = (state) => (
        propertyCities.value.find(
            (city) => city.state.value?.toLowerCase() == state?.toLowerCase()
        )?.state?.label
    );

    const selectedStateName = computed(() => (
        nameForStateAbbreviation(selectedState.value)
    ));

    const selectedProperty = computed(() => (
        properties.find(
            (property) => property.id == selectedPropertyId.value
        )
    ));

    const getPropertyCountsByState = (properties) => (
        properties.reduce(
            (counts, property) => {
                const state = property.city.state.value;
                if (!(state in counts)) {
                    counts[state] = {
                        count: 0,
                        style: map.getStatePropertyCountStyle(state),
                    };
                }
                counts[state].count += 1;
                return counts;
            },
            {}
        )
    );

    const propertyCountsByState = (
        getPropertyCountsByState(properties)
    );

    const propertiesForSelectedState = computed(() => (
        properties
            .filter(
                (property) => (
                    property.city.state.value?.toLowerCase()
                    == selectedState.value?.toLowerCase()
                )
            )
    ));

    /**
     *  Returns an object with property arrays keyed by the city ID. Note the
     *  city ID key is prefixed with a space character to preserve the key order
     *  in the resulting object, such that the cities are still in alphabetical
     *  order. Without the space character, the numeric city ID keys will be
     *  reorganized in ascending order automatically by Javascript.
     */
    const organizePropertiesByCityId = (properties) => (
        properties
            .sort(
                (a, b) => {
                    if (a.city.name === b.city.name) return 0;
                    return a.city.name > b.city.name ? 1 : -1;
                }
            )
            .reduce(
                (propertiesByCityId, property) => {
                    const cityId = property.city.id;
                    const key = ' ' + cityId;
                    if (!(key in propertiesByCityId)) {
                        propertiesByCityId[key] = [];
                    }
                    propertiesByCityId[key].push(property);
                    return propertiesByCityId;
                },
                {}
            )
    );

    const propertiesByCityId = computed(() => (
        organizePropertiesByCityId(properties)
    ));

    const propertiesByCityIdForSelectedState = computed(() => (
        organizePropertiesByCityId(propertiesForSelectedState.value)
    ));

    const propertyCitiesForSelectedState = computed(() => (
        propertyCities.value.filter(
            (city) => (
                city.state.value?.toLowerCase()
                == selectedState.value?.toLowerCase()
            )
        )
    ));

    const isCitySelected = (city) => (
        selectedCity.value?.id == city.id
    );

    const isCityHovered = (city) => (
        hoveredCity.value?.id == city.id
    );

    const isCityInSelectedState = (city) => (
        propertyCitiesForSelectedState.value.find(
            (c) => c.id == city.id
        ) !== undefined
    );

    const cityHasMultipleProperties = (city) => (
        propertiesByCityId.value[' ' + city.id].length > 1
    );

    const propertiesForCity = (city) => (
        propertiesByCityId.value[' ' + city.id]
    );

    const propertiesForSelectedCity = computed(() => (
        propertiesForCity(selectedCity.value)
    ));

    const propertyCountForSelectedState = computed(() => (
        propertiesForSelectedState.value.length
    ));

    const unitCountForSelectedState = computed(() => (
        propertiesForSelectedState.value
            .reduce(
                (unitCount, property) => (
                    unitCount + parseInt(property.unit_count || 0, 10)
                ),
                0
            )
    ));

    const stateForElement = ($state) => (
        $state.attr('data-state')
    );

    const handleBackButton = async () => {

        if (selectedCity.value !== null) {
            await map.hideCityPopup(selectedCity.value);
        }

        selectedCity.value = null;
        isCityDropdownActive.value = false;

        await nextTick();

        await map.zoomOut();

        selectedState.value = null;

        await nextTick();

        map.prepareStateDetailsPopup();
    };

    const handleHorizontalResize = async () => {

        selectedState.value = null;

        selectedCity.value = null;
        hoveredCity.value = null;

        isCityDropdownActive.value = false;

        await nextTick();

        map.reset();

        removePropertyListListeners();
        registerPropertyListListeners();
    };

    const setSelectedState = async (state) => {

        selectedState.value = state;

        await nextTick();

        map.zoomInOn(state);
    };

    const setSelectedCity = async (city) => {

        const previousCity = selectedCity.value;

        if (
            previousCity !== null
            && city?.id != previousCity.id
        ) {
            await map.hideCityPopup(previousCity);
        }

        selectedCity.value = city;

        if (city !== null) {

            selectedPropertyId.value = propertiesForCity(city)[0].id;

            await map.showCityPopup(city);
        }
    };

    const isPropertySelected = (property) => (
        selectedPropertyId.value == property.id
    );

    const setSelectedProperty = (property) => {
        selectedPropertyId.value = property.id;
    };

    const getNextProperty = () => {

        const currentIndex = (
            propertiesForSelectedCity.value.findIndex(
                (p) => p.id == selectedPropertyId.value
            )
        );

        const nextIndex = (
            currentIndex === propertiesForSelectedCity.value.length - 1
                ? currentIndex
                : currentIndex + 1
        );

        return propertiesForSelectedCity.value[nextIndex];
    };

    const getPreviousProperty = () => {

        const currentIndex = (
            propertiesForSelectedCity.value.findIndex(
                (p) => p.id == selectedPropertyId.value
            )
        );

        const previousIndex = (
            currentIndex === 0
                ? currentIndex
                : currentIndex - 1
        );

        return propertiesForSelectedCity.value[previousIndex];
    };

    const registerStateEventListeners = () => {

       const $states = els.$statesWithProperties();

       $states
            .on('mouseenter', (event) => {
                if (selectedState.value !== null) {
                    return;
                }

                map.highlightState(
                    stateForElement(
                        $(event.currentTarget)
                    )
                );
            })
            .on('mouseleave', (event) => {
                if (selectedState.value !== null) {
                    return;
                }

                map.unhighlightState(
                    stateForElement(
                        $(event.currentTarget)
                    )
                );
            })
            .on('click', async (event) => {
                if (selectedState.value !== null) {
                    return;
                }

                const state = (
                    stateForElement(
                        $(event.currentTarget)
                    )
                );

                if (isInLinkedMode()) {
                    window.location.href = (
                        `/our-properties/#/map/${state.toLowerCase()}`
                    );
                    return;
                }

                await scrollTo(
                    $('[data-tabbed] [data-toggle]'),
                    {
                        disableHeader: true,
                        accountForHeader: false,
                    }
                );

                setSelectedState(state);
            });
    };

    const registerPropertyListListeners = () => {

        propertyCities.value.forEach((city) => {

            const $list = els.$cityPopupPropertyList(city.id);
            const $items = els.$cityPopupPropertyListItems(city.id);

            if (
                !mobile({ tablet: true })
                && $items.length >= 2
            ) {

                let timeout = null;

                $list.on(
                    'wheel',
                    (event) => {

                        event.originalEvent.preventDefault();
                        event.originalEvent.stopPropagation();

                        if (lethargy.check(event.originalEvent) === false) {
                            return;
                        }

                        if (timeout === null) {
                            handleCityPopupPropertyListWheel(event);
                        }

                        clearTimeout(timeout);
                        timeout = setTimeout(
                            () => timeout = null,
                            80
                        );

                    }
                );
            }
        });
    };

    const removePropertyListListeners = () => {

        const $lists = els.$cityPopupPropertyLists();

        $lists.each((_, element) => {

            const $list = $(element);

            $list.off('wheel');
        });
    };

    const handleCityDropdownOptionClick = async (city) => {

        await setSelectedCity(city);

        if (!isScreenAtLeast('md')) {

            const $popup = els.$cityPopup(city.id);

            await closeCityDropdown();

            if (!ScrollTrigger.isInViewport($popup.get(0), 1)) {
                await scrollTo(
                    $popup,
                    {
                        alignment: 'bottom',
                        accountForHeader: false,
                        disableHeader: true,
                    }
                );
            }
        }
    };

    const handleCityDropdownOptionPropertyClick = async (property) => {

        if (selectedCity.value?.id !== property.city.id) {
            await setSelectedCity(property.city);
        }

        if (!isScreenAtLeast('md')) {

            const $popup = els.$cityPopup(property.city.id);

            await closeCityDropdown();

            if (!ScrollTrigger.isInViewport($popup.get(0), 1)) {
                await scrollTo(
                    $popup,
                    {
                        alignment: 'bottom',
                        accountForHeader: false,
                        disableHeader: true,
                    }
                );
            }
        }

        await map.shiftPropertyListTo(property);

        setSelectedProperty(property);
    };

    const collectCityMarkerClasses = (city) => {

        const classes = [];

        classes.push(
            cityHasMultipleProperties(city)
                ? 'border-blue-dark bg-yellow'
                : 'border-transparent bg-transparent'
        );

        classes.push(
            (
                isCityHovered(city)
                || isCitySelected(city)
            )
                ? 'border-red [&_*]:bg-red [&_*]:~size-[.625rem]/[1.625rem]'
                : '[&_*]:bg-blue-dark'
        );

        return classes.join(' ');
    };

    const handleCityMarkerClick = async (city) => {

        await setSelectedCity(city);

        if (!isScreenAtLeast('md')) {

            const $popup = els.$cityPopup(city.id);

            if (!ScrollTrigger.isInViewport($popup.get(0), 1)) {
                await scrollTo(
                    $popup,
                    {
                        alignment: 'bottom',
                        accountForHeader: false,
                        disableHeader: true,
                    }
                );
            }
        }
    };

    const handleCityMarkerMouseEnter = (city) => {

        hoveredCity.value = city;
    };

    const handleCityMarkerMouseLeave = (city) => {

        hoveredCity.value = null;
    };

    const closeCityDropdown = async () => {

        isCityDropdownActive.value = false;

        await map.closeCityDropdown();
    };

    const openCityDropdown = async () => {

        isCityDropdownActive.value = true;

        await map.openCityDropdown();
    };

    const handleCityPopupPropertyMouseEnter = (property) => {

        if (map.isZooming()) {
            return;
        }

        selectedPropertyId.value = property.id;
    };

    const handleCityPopupPropertyMouseLeave = (property) => {

        if (!isScreenAtLeast('md')) {
            return;
        }

        if (map.isZooming()) {
            return;
        }

        map.hideCityPopup(property.city);
    };

    const handleCityPopupCloseButtonClick = (city) => {

        selectedCity.value = null;

        map.hideCityPopup(city);
    };

    const handleCityDropdownClick = () => {

        if (isCityDropdownActive.value) {
            closeCityDropdown();
        } else {
            openCityDropdown();
        }
    };

    const handleCityPopupPropertyListWheel = async (event) => {

        const property = (
            event.originalEvent.deltaY > 0
                ? getNextProperty()
                : getPreviousProperty()
        );


        await map.shiftPropertyListTo(property);

        setSelectedProperty(property);
    };

    const handleCityPopupPropertyListItemClick = async (property) => {

        await map.shiftPropertyListTo(property);

        setSelectedProperty(property);
    };

    const registerWindowResizeListener = () => {

        let timer = null;
        let adjustmentCount = 0;

        $(window).on('resize:horizontally', () => {

            resizing.value = true;

            if (adjustmentCount === 0) {
                handleHorizontalResize();
                adjustmentCount += 1;
            }

            clearTimeout(timer);

            timer = (
                setTimeout(
                    () => {
                        handleHorizontalResize();
                        adjustmentCount = 0;
                        resizing.value = false
                    },
                    RESIZE_TIMEOUT
                )
            );
        });
    };

    onMounted(() => {

        lethargy = new Lethargy(7, 1, 0.05);

        map.initialize({
            cities: propertyCities.value,
            requestedInitialZoom,
        });

        registerStateEventListeners();

        registerPropertyListListeners();

        registerWindowResizeListener();

        loading.value = false;
    });

    return {
        isMobile,
        loading,
        resizing,
        properties,
        selectedState,
        setSelectedState,
        selectedStateName,
        setSelectedCity,
        selectedPropertyId,
        selectedProperty,
        isPropertySelected,
        propertyCountsByState,
        propertyCities,
        propertyCitiesForSelectedState,
        propertiesByCityIdForSelectedState,
        propertiesForCity,
        propertiesForSelectedCity,
        isCityInSelectedState,
        cityHasMultipleProperties,
        propertyCountForSelectedState,
        unitCountForSelectedState,
        getStatePropertyCountStyle: map.getStatePropertyCountStyle,
        handleBackButton,
        collectCityMarkerClasses,
        handleCityMarkerClick,
        handleCityMarkerMouseEnter,
        handleCityMarkerMouseLeave,
        handleCityPopupPropertyMouseEnter,
        handleCityPopupPropertyMouseLeave,
        handleCityPopupPropertyListItemClick,
        handleCityPopupCloseButtonClick,
        handleCityDropdownClick,
        handleCityDropdownOptionClick,
        handleCityDropdownOptionPropertyClick,
        isCityDropdownActive,
    };
};

const initialize = () => {

    const app = createApp({ setup: setupApp });

    const element = els.$app().get(0);

    window.vues.propertiesMap = app.mount(element);
};

export default {
    initialize,
};

